import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { useLenis } from "lenis/react";
import { BRAND, NAV_LINKS } from "../data/content";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const lenis = useLenis();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goTo = (id) => {
    setOpen(false);
    const el = document.getElementById(id);
    if (el && lenis) lenis.scrollTo(el, { offset: -80, duration: 1.4 });
    else if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <>
      <motion.header
        data-testid="navbar"
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 1, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
        className={`fixed top-0 left-0 right-0 z-[100] transition-all duration-500 ${
          scrolled ? "py-3 glass-strong border-b border-white/10" : "py-5 bg-transparent"
        }`}
      >
        <nav className="mx-auto max-w-7xl px-6 md:px-10 flex items-center justify-between">
          <button
            data-testid="nav-logo"
            onClick={() => goTo("home")}
            className="flex items-center gap-3 group"
          >
            <img src={BRAND.logo} alt="GK Brandify" className="w-8 h-8 rounded-lg object-cover ring-1 ring-gk-gold/30" />
            <span className="hidden sm:block font-cinzel text-base tracking-[0.25em] text-gold-metallic">
              GK BRANDIFY
            </span>
          </button>

          <div className="hidden lg:flex items-center gap-9">
            {NAV_LINKS.map((link) => (
              <button
                key={link.id}
                data-testid={`nav-link-${link.id}`}
                onClick={() => goTo(link.id)}
                className="relative font-inter text-sm tracking-wide text-white/70 hover:text-white transition-colors duration-300 group"
              >
                {link.label}
                <span className="absolute -bottom-1.5 left-0 h-px w-0 bg-gk-gold transition-all duration-400 group-hover:w-full" />
              </button>
            ))}
          </div>

          <button
            data-testid="nav-cta"
            onClick={() => goTo("contact")}
            className="hidden lg:inline-flex btn-gold font-inter text-sm font-medium px-6 py-2.5 rounded-full"
          >
            Start Your Project
          </button>

          <button
            data-testid="nav-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden text-white p-2"
            aria-label="Toggle menu"
          >
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </nav>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0, x: "100%" }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: "100%" }}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
            className="fixed inset-0 z-[99] lg:hidden glass-strong flex flex-col items-center justify-center gap-8 px-8"
          >
            <div className="ambient-emerald" style={{ width: 300, height: 300, top: "20%", right: "-10%" }} />
            {NAV_LINKS.map((link, i) => (
              <motion.button
                key={link.id}
                data-testid={`mobile-nav-link-${link.id}`}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 + i * 0.06 }}
                onClick={() => goTo(link.id)}
                className="font-cinzel text-3xl text-white/85 hover:text-gk-gold transition-colors"
              >
                {link.label}
              </motion.button>
            ))}
            <button
              onClick={() => goTo("contact")}
              className="btn-gold font-inter font-medium px-8 py-3 rounded-full mt-4"
            >
              Start Your Project
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
